import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import config from "../config";
import { authService } from "../services/authService";

const { api } = config;

// default
axios.defaults.baseURL = api.API_URL;
// content type - set for POST, PUT, and PATCH requests
axios.defaults.headers.post["Content-Type"] = "application/json";
axios.defaults.headers.put["Content-Type"] = "application/json";
axios.defaults.headers.patch["Content-Type"] = "application/json";
axios.defaults.headers.common["Content-Type"] = "application/json";

/**
 * Get auth token using AuthService
 * Centralizes token retrieval - AuthService handles sessionStorage parsing internally
 * 
 * BEHAVIOR PRESERVED: AuthService.getToken() reads from sessionStorage the same way
 * as the old getAuthToken() function, so behavior is identical.
 */
const getAuthToken = () => {
  // AuthService.getToken() reads from sessionStorage (fallback) when no Redux state provided
  // This matches the old behavior exactly
  return authService.getToken();
};

// Initialize token on module load (for initial axios setup)
const token = getAuthToken();
if (token) {
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;
}

// Flag to prevent multiple refreshes
let isRefreshing = false;

/**
 * Queue item for failed requests waiting for token refresh
 */
interface FailedQueueItem {
  resolve: (token: string | null) => void;
  reject: (error: unknown) => void;
}

let failedQueue: FailedQueueItem[] = [];
let refreshEndpointAvailable = true; // Track if refresh endpoint exists (set to false if 404)

// Helper to check if we're on an auth page (replaces redirect flag check)
// Loop prevention: pathname check ensures we only redirect when NOT already on auth pages
const isRedirectingToLogin = (): boolean => {
  const pathname = window.location.pathname;
  return pathname === "/login" || pathname === "/otp-verification" || pathname === "/forgot-password";
};

// Helper to check if we're on an auth page
const isAuthPage = () => {
  const path = window.location.pathname;
  return path === "/login" || path === "/otp-verification" || path === "/forgot-password";
};

/**
 * DEV-ONLY: true when running the field-app prototype (`/field*`) in dev.
 *
 * The field app has no backend yet, so its bootstrap master-data calls 401
 * against the real API. Without this guard the 401 handler would wipe the
 * (mock) session and bounce to /login, making the prototype impossible to
 * open. On the field prototype we therefore swallow the 401 instead of
 * logging out. Production builds and the desktop app are never affected.
 * Remove once the field backend + auth are wired.
 */
const isFieldPrototype = () =>
  import.meta.env.DEV && window.location.pathname.startsWith("/field");

/**
 * Process failed request queue
 * 
 * @param error - Error to reject with, or null if successful
 * @param token - Token to resolve with (if successful)
 */
const processQueue = (error: unknown, token: string | null = null): void => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Request interceptor to attach token dynamically
axios.interceptors.request.use(
  (config) => {
    // Debug: Log request to console (helps when network tab doesn't show it)
    if (process.env.NODE_ENV === 'development') {
      console.log('🌐 API Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        baseURL: config.baseURL,
        fullURL: `${config.baseURL || ''}${config.url || ''}`,
        data: config.data,
        headers: config.headers
      });
    }
    
    // CRITICAL: Block ALL API calls if we're on auth pages
    // Loop prevention: pathname check ensures we only block when actually on auth pages
    if (isAuthPage()) {
      return Promise.reject(new Error("API calls blocked on auth page"));
    }
    
    // CRITICAL: Always get fresh token from storage FIRST (before checking validity)
    // This ensures we always try to attach token even if axios defaults are stale
    const token = getAuthToken();
    
    if (token) {
      // Token exists in storage - attach it immediately
      config.headers["Authorization"] = "Bearer " + token;
      // Also update axios defaults to ensure consistency
      axios.defaults.headers.common["Authorization"] = "Bearer " + token;
      
      // Don't validate token here - let the API call proceed
      // Backend will return 401 if token is invalid, and response interceptor will handle it
    } else {
      // No token found in storage
      // Clear axios defaults
      delete axios.defaults.headers.common["Authorization"];
      
      // CRITICAL: For protected routes, redirect to login
      // But allow the request to proceed - backend will return 401 if needed
      // This prevents blocking legitimate API calls that might work without auth
      // Loop prevention: pathname check ensures we only redirect when NOT already on login page
      if (!isAuthPage()) {
        // Check if this looks like a protected API call
        const isApiCall = config.url && (
          config.url.includes("/api/") || 
          config.url.startsWith("/") && !config.url.startsWith("/public")
        );
        
        // Only redirect for API calls that require auth
        if (isApiCall) {
          // window.location.replace() is idempotent - safe to call multiple times
          window.location.replace("/login");
          return Promise.reject(new Error("No token found - redirecting to login"));
        }
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// intercepting to capture errors
axios.interceptors.response.use(
  function (response) {
    // Handle 204 No Content responses
    if (response.status === 204) {
      // Return a default structure for 204 responses to prevent breaking the app
      return {
        items: [],
        pageNumber: 1,
        pageSize: 50,
        totalCount: 0,
        totalPages: 0
      };
    }
    
    // Skip transformation for blob/arraybuffer responses to prevent corruption
    // When responseType is 'blob' or 'arraybuffer', return full response to preserve binary data
    if (response.config?.responseType === 'blob' || response.config?.responseType === 'arraybuffer') {
      return response;
    }
    
    return response.data ? response.data : response;
  },
  async function (error) {
    const originalRequest = error.config;

    // Canceled/aborted requests are intentional - pass through without logging or transforming
    // Prevents "Failed to load" toasts and console errors when user navigates away or StrictMode aborts
    // Covers: native AbortError, Axios ERR_CANCELED/CanceledError, legacy CancelToken
    const isCanceled =
      error.code === "ERR_CANCELED" ||
      error.name === "AbortError" ||
      error.name === "CanceledError" ||
      axios.isCancel(error);
    if (isCanceled) {
      return Promise.reject(error);
    }
    
    // Handle 404 for ClientRentalConfiguration as successful "not found" (not an error)
    // This prevents browser console from showing it as an error
    // Check multiple possible URL locations (axios stores URLs in different places)
    const requestUrl = originalRequest?.url || '';
    const requestBaseUrl = originalRequest?.baseURL || '';
    const responseUrl = error.response?.config?.url || '';
    const responseBaseUrl = error.response?.config?.baseURL || '';
    const fullUrl = error.response?.config?.url || originalRequest?.url || '';
    
    // Combine all possible URL sources for checking
    const urlToCheck = `${requestUrl}${requestBaseUrl}${responseUrl}${responseBaseUrl}${fullUrl}`;
    
    // Check if this is a 404 for a RentalConfiguration endpoint (client- OR tenant-level).
    // "No config yet" is a normal empty state, not an error — return null so the feature
    // can show its create/default form instead of an error card.
    const isRentalConfig404 = error.response?.status === 404 &&
                                     (urlToCheck.includes('ClientRentalConfiguration') ||
                                      urlToCheck.includes('TenantRentalConfiguration'));

    if (isRentalConfig404) {
      // Return null as a successful response instead of rejecting
      // This treats "no config found" as normal, not an error
      // Note: Network tab will still show 404 in red (that's how browsers display HTTP status codes)
      // But no console errors will appear and the code treats it as a normal "not found" case
      if (process.env.NODE_ENV === 'development') {
        console.log("✅ Handling 404 for RentalConfiguration as normal 'not found' (not an error)", {
          requestUrl,
          responseUrl,
          fullUrl
        });
      }
      return Promise.resolve(null);
    }

    // CRITICAL: If we're on auth pages, reject immediately without any refresh attempts
    // Loop prevention: pathname check ensures we only block when actually on auth pages
    if (isAuthPage()) {
      return Promise.reject("On auth page - no refresh attempts");
    }

    // Any status codes that falls outside the range of 2xx cause this function to trigger
    let message;
    
    // Handle 401 Unauthorized - DISABLED TOKEN REFRESH: Force full re-login
    if (error.response?.status === 401 && !originalRequest._retry) {
      // CRITICAL: If already on auth page, just reject (don't do anything else)
      // Loop prevention: pathname check ensures we only redirect when NOT already on auth pages
      if (isAuthPage()) {
        return Promise.reject("Unauthorized - already on auth page");
      }

      // DEV field prototype: swallow the 401 (no backend) — keep the mock
      // session and the prototype rendered instead of bouncing to /login.
      if (isFieldPrototype()) {
        return Promise.reject("Field prototype (dev) - ignoring 401");
      }

      // TOKEN REFRESH LOGIC COMMENTED OUT - Force full re-authentication
      // ================================================================
      // When token expires, immediately clear all session data and redirect to login
      // User must login again with new OTP
      // Loop prevention: pathname check above ensures we're NOT already on login page
      // window.location.replace() is idempotent - multiple calls with same URL are harmless
      
      // Clear Redux Persist storage
      try {
        sessionStorage.removeItem('persist:root');
      } catch (e) {
        console.error("Error clearing Redux Persist storage:", e);
      }
      
      // Clear axios default headers
      delete axios.defaults.headers.common["Authorization"];
      
      // Redirect to login immediately (use replace to prevent back button issues)
      // Login page will dispatch logoutUser() to clear Redux state
      window.location.replace("/login");
      
      return Promise.reject("Token expired - redirecting to login");
    }

    // Handle 405 Method Not Allowed - often indicates invalid tenantId or wrong HTTP method
    if (error.response?.status === 405) {
      message = "Method Not Allowed - The request method is not supported for this resource";
      return Promise.reject(message);
    }

    // Handle timeout errors - provide user-friendly message
    // Note: Backend may still process the request successfully even after timeout
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      const isWriteOperation = originalRequest?.method && 
                               ['POST', 'PUT', 'PATCH'].includes(originalRequest.method.toUpperCase());
      
      if (isWriteOperation) {
        message = "Request timed out. The operation may still be processing on the server. Please check if your changes were saved.";
      } else {
        message = "Request timed out. Please try again.";
      }
      
      // Create error object with timeout flag for components to handle differently
      const timeoutError = new Error(message);
      (timeoutError as any).isTimeout = true;
      (timeoutError as any).code = 'ECONNABORTED';
      (timeoutError as any).originalError = error;
      
      if (process.env.NODE_ENV === 'development') {
        console.warn("⏱️ API Timeout:", {
          url: originalRequest?.url,
          method: originalRequest?.method,
          message: "Request exceeded timeout limit. Backend may still be processing."
        });
      }
      
      return Promise.reject(timeoutError);
    }

    // Default Error Handling (including failed refresh)
    switch (error.response?.status) {
      case 500:
        message = "Internal Server Error";
        break;
      case 401:
        message = "Invalid credentials - Token expired";
        // Only clear auth and redirect if we're not already on auth pages
        // Loop prevention: pathname check ensures we only redirect when NOT already on login page
        // (DEV field prototype is exempt — no backend, keep the mock session.)
        if (!isAuthPage() && !isFieldPrototype()) {
          // Clear Redux Persist storage
          try {
            sessionStorage.removeItem('persist:root');
          } catch (e) {
            console.error("Error clearing Redux Persist storage:", e);
          }
          
          // Clear axios default headers
          delete axios.defaults.headers.common["Authorization"];
          
          // Use replace instead of href to prevent back button issues and loops
          // window.location.replace() is idempotent - safe to call multiple times
          window.location.replace("/login");
        }
        break;
      case 400:
        // Bad Request - extract actual error message from API response
        message = error.response?.data?.message 
          || error.response?.data?.error 
          || error.response?.data?.title
          || "Bad Request - Please check your input";
        // Preserve error object for better debugging
        const errorWithResponse = new Error(message);
        (errorWithResponse as any).response = error.response;
        (errorWithResponse as any).status = 400;
        return Promise.reject(errorWithResponse);
      case 404:
        message = "Sorry! the data you are looking for could not be found";
        break;
      default:
        // Try to extract error message from API response
        message = error.response?.data?.message 
          || error.response?.data?.error 
          || error.response?.data?.title
          || error.message 
          || error;
    }
    
    // Preserve full error object for debugging
    // Skip logging 404 errors for ClientRentalConfiguration - "not found" is normal, not an error
    const shouldSkipLogging = error.response?.status === 404 && 
                              originalRequest?.url && 
                              originalRequest.url.includes('ClientRentalConfiguration');
    
    if (process.env.NODE_ENV === 'development' && error.response && !shouldSkipLogging) {
      console.error("❌ API Error Response:", {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        url: originalRequest?.url,
        method: originalRequest?.method
      });
    }
    
    return Promise.reject(message);
  }
);

/**
 * Sets the default authorization token in axios headers
 * 
 * @param token - JWT token string
 */
const setAuthorization = (token: string): void => {
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;
  // Re-enable refresh endpoint check (new login, endpoint might be available)
  refreshEndpointAvailable = true;
};

/**
 * APIClient - Typed HTTP client wrapper around axios
 * 
 * Uses TypeScript generics to provide type-safe API calls.
 * Response types match backend payloads exactly - no transformation.
 */
class APIClient {
  /**
   * Fetches data from given url with typed response
   * 
   * @template T - Response type (e.g., PaginatedResponse<LeadItem>)
   * @param url - API endpoint URL
   * @param params - Query parameters object
   * @param config - Optional config (signal for AbortController to cancel on unmount)
   * @returns Promise<T> - Typed response matching backend structure
   */
  get = <T = any>(
    url: string,
    params?: Record<string, string | number | boolean | undefined>,
    config?: { signal?: AbortSignal }
  ): Promise<T> => {
    let response;
    const axiosConfig: { timeout: number; signal?: AbortSignal } = {
      timeout: 10000, // 10 second timeout
    };
    if (config?.signal) {
      axiosConfig.signal = config.signal;
    }

    // Check if params exists and has keys (not empty object)
    if (params && Object.keys(params).length > 0) {
      const paramKeys: string[] = [];

      Object.keys(params).forEach((key) => {
        const value = params[key];
        if (value !== undefined && value !== null) {
          paramKeys.push(`${key}=${encodeURIComponent(String(value))}`);
        }
      });

      const queryString = paramKeys.length > 0 ? paramKeys.join("&") : "";
      
      // Check if URL already has query parameters
      const separator = url.includes("?") ? "&" : "?";
      response = axios.get<T>(`${url}${separator}${queryString}`, axiosConfig);
    } else {
      // No params or empty object - use URL as-is
      response = axios.get<T>(`${url}`, axiosConfig);
    }

    // Axios interceptor already extracts response.data, so return response directly
    return response as Promise<T>;
  };
  
  /**
   * POST request with typed response
   * 
   * @template T - Response type
   * @param url - API endpoint URL
   * @param data - Request payload
   * @returns Promise<T> - Typed response matching backend structure
   */
  create = <T = any>(url: string, data?: unknown): Promise<T> => {
    // Axios interceptor already extracts response.data, so return response directly
    return axios.post<T>(url, data, {
      timeout: 30000, // Increased to 30 seconds to handle slow backend processing
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      }
    }) as Promise<T>;
  };
  
  /**
   * PATCH request with typed response
   * 
   * @template T - Response type
   * @param url - API endpoint URL
   * @param data - Request payload
   * @returns Promise<T> - Typed response matching backend structure
   */
  update = <T = any>(url: string, data?: unknown): Promise<T> => {
    // Axios interceptor already extracts response.data, so return response directly
    return axios.patch<T>(url, data, {
      timeout: 30000 // Increased to 30 seconds to handle slow backend processing
    }) as Promise<T>;
  };

  /**
   * PUT request with typed response
   * 
   * @template T - Response type
   * @param url - API endpoint URL
   * @param data - Request payload
   * @returns Promise<T> - Typed response matching backend structure
   */
  put = <T = any>(url: string, data?: unknown): Promise<T> => {
    // Ensure data is properly serialized as JSON
    // axios will automatically serialize objects when Content-Type is application/json
    // Axios interceptor already extracts response.data, so return response directly
    return axios.put<T>(url, data, {
      timeout: 30000, // Increased to 30 seconds to handle slow backend processing
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      // Ensure axios serializes the data properly
      transformRequest: [(data) => {
        // If data is already a string, return it; otherwise stringify it
        if (typeof data === 'string') {
          return data;
        }
        return JSON.stringify(data);
      }]
    }) as Promise<T>;
  };
  
  /**
   * DELETE request with typed response
   * 
   * @template T - Response type (defaults to void for delete operations)
   * @param url - API endpoint URL
   * @param config - Optional axios config
   * @returns Promise<T> - Typed response (usually void)
   */
  delete = <T = void>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<T> => {
    // Axios interceptor already extracts response.data, so return response directly
    return axios.delete<T>(url, { 
      ...config,
      timeout: 10000 // 10 second timeout
    }) as Promise<T>;
  };
}

/**
 * Get logged in user using AuthService
 * Centralizes user retrieval - AuthService handles sessionStorage parsing internally
 * 
 * BEHAVIOR PRESERVED: AuthService.getUser() reads from sessionStorage the same way
 * as the old getLoggedinUser() function, so behavior is identical.
 * 
 * This is mainly for non-React code (like axios interceptors).
 * For React components, use useSelector to get user from Redux store directly.
 */
const getLoggedinUser = () => {
  // AuthService.getUser() reads from sessionStorage (fallback) when no Redux state provided
  // This matches the old behavior exactly
  return authService.getUser();
};

/**
 * Validates if the current auth token is still valid
 * @returns boolean indicating if the token is valid
 */
export const isAuthTokenValid = (): boolean => {
  try {
    const user = getLoggedinUser();
    
    // If no user data, token is not valid
    if (!user) {
      return false;
    }
    
    // Standardize: use 'token' field only (jwt is legacy, removed)
    const token = user.token;
    if (!token) {
      return false;
    }
    
    // Check if token has expired (only if expiresAt is provided)
    if (user.expiresAt) {
      try {
        const expiryTime = new Date(user.expiresAt).getTime();
        const currentTime = new Date().getTime();
        
        // If token has expired, return false
        if (currentTime >= expiryTime) {
          return false;
        }
      } catch (dateError) {
        // If expiresAt is invalid format, assume token is valid (let backend handle it)
      }
    }
    
    return true;
  } catch (error) {
    console.error("Error validating auth token:", error);
    return false;
  }
};

/**
 * Validates tenantId format and ensures user data exists
 * Does NOT validate token validity - let axios interceptor handle that
 * Throws error and redirects to login only if tenantId is missing, invalid format, or fake
 * @returns string - validated tenantId
 * @throws Error if tenantId is invalid (triggers redirect to login)
 */
const getValidatedTenantId = (): string => {
  /**
   * Validates tenantId and ensures user exists
   * Redirects to login if validation fails (only if not already on auth page)
   */
  const user = getLoggedinUser();
  if (!user) {
    if (!isAuthPage()) {
      window.location.replace("/login");
    }
    throw new Error("No authenticated user - redirecting to login");
  }

  const tenantId = user.tenantId;
  if (!tenantId) {
    if (!isAuthPage()) {
      window.location.replace("/login");
    }
    throw new Error("TenantId missing - redirecting to login");
  }

  // Validate UUID format
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(tenantId)) {
    if (!isAuthPage()) {
      window.location.replace("/login");
    }
    throw new Error("Invalid tenantId format - redirecting to login");
  }

  return tenantId;
};

export { APIClient, setAuthorization, getLoggedinUser, getValidatedTenantId };
